import { get } from "lodash-es";

import { PropertyValue } from "src/api/flowTypes";
import {
  isObjectWithProperties,
  isObject,
  JsonSchema,
  isArrayOfPrimitive,
  isPrimitive,
  JsonSchemaNode,
} from "src/jsonSchema/types";
import { SchemaOptions } from "src/router/SearchParams";
import { PropertyConverter, PropertyUI } from "src/schema/schemaMappingUtils";

export const JsonRefPathPrefix = "#/$defs/";
export const JsonRefPathPrefixEntities = "#/$defs/Entities@";
export const JsonRefPathPrefixEvents = "#/$defs/Events@";
export const JsonRefPathPrefixCases = "#/$defs/case_properties@";

export type JsonRefPath = `${typeof JsonRefPathPrefix}${string}`;
export const isJsonRefPath = (path: unknown): path is JsonRefPath => {
  return (
    typeof path === "string" &&
    (path.startsWith(JsonRefPathPrefix) ||
      // draft-07 and older
      path.startsWith(JsonRefPathPrefix.replace("$defs", "definitions")))
  );
};

export const getEffectiveType = (property: PropertyUI): string | undefined => {
  const [type] = Array.isArray(property.type) ? property.type : [property.type];
  if (type === "array") {
    const items = property.items;
    const itemType = Array.isArray(items?.type) ? items?.type[0] : items?.type;
    return items?.$ref || itemType;
  }
  return type;
};

export const entityNameToJsonRefPath = (entityName: string): JsonRefPath => {
  return `${JsonRefPathPrefixEntities}${entityName}`;
};

export const eventNameToJsonRefPath = (eventName: string): JsonRefPath => {
  return `${JsonRefPathPrefixEvents}${eventName}`;
};

export const extractFieldNameFromJsonRefPath = (path: JsonRefPath): string => {
  return path.replace(JsonRefPathPrefix, "");
};

export const caseTemplateIdToJsonRefPath = (
  templateId: string,
  versionId?: string,
): JsonRefPath => {
  if (versionId) {
    return `${JsonRefPathPrefixCases}${templateId}|${versionId}` as JsonRefPath;
  }
  return `${JsonRefPathPrefixCases}${templateId}` as JsonRefPath;
};

export const getTreeSchemaWithProperty = (property: PropertyUI): JsonSchema => {
  const [type] = property.type;
  let properties: Record<string, PropertyValue | undefined>;
  if (isJsonRefPath(type)) {
    const fieldName = extractFieldNameFromJsonRefPath(type);
    const refDefinition = get(property, `_schema.$defs.["${fieldName}"]`, {});

    properties = {
      [property.fieldName]: refDefinition,
    };
  } else {
    properties = {
      [property.fieldName]: property._schemaNode,
    };
  }

  return {
    ...(property._schema ?? {}),
    properties,
  } as JsonSchema;
};

export const hasJSONTreePreview = (
  property: PropertyUI,
  type: SchemaOptions,
): boolean => {
  const schemaNode = PropertyConverter.uiToBEProperty(
    property,
    type,
  ) as JsonSchemaNode;
  return (
    schemaNode &&
    !isPrimitive(schemaNode) &&
    !isArrayOfPrimitive(schemaNode) &&
    !(isObject(schemaNode) && !isObjectWithProperties(schemaNode))
  );
};

export type DecodedJsonRefPath = {
  metaType: "entity" | "event" | "case";
  type: string;
  versionId?: string;
};

export const decodeJsonRefPath = (
  path: string,
): DecodedJsonRefPath | undefined => {
  if (path.startsWith(JsonRefPathPrefixEntities)) {
    return {
      metaType: "entity" as const,
      type: path.slice(JsonRefPathPrefixEntities.length),
    };
  }
  if (path.startsWith(JsonRefPathPrefixEvents)) {
    return {
      metaType: "event" as const,
      type: path.slice(JsonRefPathPrefixEvents.length),
    };
  }
  if (path.startsWith(JsonRefPathPrefixCases)) {
    const caseInfo = path.slice(JsonRefPathPrefixCases.length);

    if (caseInfo.includes("|")) {
      const [templateId, versionId] = caseInfo.split("|");
      return {
        metaType: "case" as const,
        type: templateId,
        versionId: versionId,
      };
    }
    return {
      metaType: "case" as const,
      type: caseInfo,
    };
  }
  return undefined;
};

export const isReferencingEntity = (path: JsonRefPath): boolean => {
  return path.startsWith(JsonRefPathPrefixEntities);
};

export const isReferencingEvent = (path: JsonRefPath): boolean => {
  return path.startsWith(JsonRefPathPrefixEvents);
};

export const isReferencingCase = (path: JsonRefPath): boolean => {
  return path.startsWith(JsonRefPathPrefixCases);
};
